import { useMemo } from 'react';
import { Ticket, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { cn, formatScore, scoreColor } from '@/lib/utils';
import type { HomeMyBetsQuery } from '@/gql/graphql';

type BetItem = NonNullable<NonNullable<HomeMyBetsQuery['myBets']>['items']>[number];

const MAX_BETS = 3;

interface MyBetsPreviewProps {
    bets: BetItem[];
}

export function MyBetsPreview({ bets }: MyBetsPreviewProps) {
    const { t } = useTranslation('home');
    const { t: tb } = useTranslation('bets');

    const previewBets = bets.slice(0, MAX_BETS);

    if (bets.length === 0) {
        return (
            <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Ticket size={20} className="text-emerald-500" />
                    {t('sections.myBets')}
                </h2>
                <div className="py-10 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                    <Ticket size={32} className="mx-auto mb-2 text-slate-200" />
                    <p className="text-slate-400 text-sm font-medium mb-2">{t('myBets.empty')}</p>
                    <Link to="/races" className="text-emerald-600 font-bold text-sm hover:underline">
                        {t('myBets.emptyAction')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Ticket size={20} className="text-emerald-500" />
                    {t('sections.myBets')}
                </h2>
                <Link
                    to="/my-bets"
                    className="text-sm font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                >
                    {t('seeAll')}
                    <ArrowRight size={14} />
                </Link>
            </div>

            <div className="relative">
                <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory md:grid md:grid-cols-2 md:overflow-visible md:pb-0">
                    {previewBets.map((bet) => (
                        <BetPreviewCard key={bet.id} bet={bet} tb={tb} />
                    ))}
                </div>
                {previewBets.length > 1 && (
                    <div className="absolute top-0 right-0 bottom-2 w-8 bg-gradient-to-l from-slate-50 to-transparent pointer-events-none md:hidden" />
                )}
            </div>
        </div>
    );
}

function BetPreviewCard({ bet, tb }: { bet: BetItem; tb: (key: string) => string }) {
    const race = bet.race;
    const isOpen = race?.status === 'OPEN';
    const hasScore = bet.score != null;
    const rankings = useMemo(() => bet.rankings ?? [], [bet.rankings]);
    const genders = useMemo(() => [...new Set(rankings.map(r => r.gender).filter(Boolean))] as string[], [rankings]);

    return (
        <Link
            to={`/races/${race?.id}`}
            className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-emerald-100 transition-all group min-w-[240px] snap-start flex flex-col md:min-w-0"
        >
            <div className="flex items-start justify-between mb-2">
                <h3 className="font-bold text-slate-900 group-hover:text-emerald-700 transition-colors text-sm line-clamp-1 flex-1">
                    {race?.name}
                </h3>
                {hasScore && (
                    <span className={cn("text-sm font-black ml-2 flex-shrink-0", scoreColor(bet.score ?? 0))}>
                        {formatScore(bet.score ?? 0)} pts
                    </span>
                )}
            </div>

            <div className="flex items-center gap-1.5 mb-3">
                {genders.map((g) => (
                    <span
                        key={g}
                        className={cn(
                            "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide",
                            g === 'MALE' ? "bg-blue-50 text-blue-600" : "bg-pink-50 text-pink-600"
                        )}
                    >
                        {g === 'MALE' ? tb('gender.MALE') : tb('gender.FEMALE')}
                    </span>
                ))}
                <span className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide",
                    isOpen ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                )}>
                    {isOpen ? tb('list.pending') : tb('list.score')}
                </span>
            </div>

            <RankingsPreview rankings={rankings} tb={tb} />
        </Link>
    );
}

function RankingsPreview({ rankings, tb }: { rankings: BetItem['rankings']; tb: (key: string) => string }) {
    const ranked = [...(rankings ?? [])]
        .filter(r => (r.position ?? 0) > 0)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .slice(0, 3);

    if (ranked.length === 0) return null;

    return (
        <div className="space-y-1 mt-auto">
            {ranked.map((r, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs">
                    <span className="w-4 h-4 rounded bg-emerald-100 text-emerald-700 text-[10px] font-black flex items-center justify-center flex-shrink-0">
                        {r.position}
                    </span>
                    <span className="text-slate-600 truncate">{r.runner?.name}</span>
                    <span className={cn(
                        "text-[10px] font-bold ml-auto flex-shrink-0",
                        r.gender === 'MALE' ? "text-blue-400" : "text-pink-400"
                    )}>
                        {r.gender === 'MALE' ? tb('gender.MALE').charAt(0) : tb('gender.FEMALE').charAt(0)}
                    </span>
                </div>
            ))}
        </div>
    );
}
